/**
 * <gift-options> custom element for the cart drawer.
 *
 * - A "Gift ribbon" checkbox adds/removes the Gift Ribbon product. Its quantity always
 *   matches the total quantity of the real (non-gift) products in the cart.
 * - The gift note textarea, when it has text, adds the Gift Note product (quantity always 1)
 *   and auto-checks the gift ribbon. Clearing the note removes the Gift Note line.
 *
 * Gift lines are tagged with the cart line property "_gift_option" so they can be found
 * and managed regardless of cart position, and are hidden from quantity/remove controls.
 *
 * To keep typing smooth, note edits are debounced and, while the note field is focused,
 * the drawer body is not re-rendered (which would move the cursor). The visible drawer is
 * reconciled when the field loses focus.
 */
(function () {
  if (window.customElements.get('gift-options')) {
    return;
  }

  const GIFT_OPTION_PROPERTY = '_gift_option';
  const RIBBON_KEY = 'ribbon';
  const NOTE_KEY = 'note';
  const NOTE_DEBOUNCE_MS = 250;

  class GiftOptions extends HTMLElement {
    connectedCallback() {
      this.ribbonCheckbox = this.querySelector('[data-gift-ribbon-checkbox]');
      this.noteTextarea = this.querySelector('[data-gift-note-textarea]');

      this.ribbonVariantId = this.getAttribute('ribbon-variant-id') || '';
      this.noteVariantId = this.getAttribute('note-variant-id') || '';

      this._busy = false;
      this._noteDebounce = null;
      this._pendingReconcile = false;
      this._noteDirty = false; // note edited since the last sync started
      this._pendingSyncs = 0;
      this._syncChain = Promise.resolve();
      this._checkingOut = false;

      if (this.ribbonCheckbox) {
        this._onRibbonChange = () => this.handleRibbonToggle();
        this.ribbonCheckbox.addEventListener('change', this._onRibbonChange);
      }

      if (this.noteTextarea) {
        this._onNoteInput = () => this.handleNoteInput();
        this.noteTextarea.addEventListener('input', this._onNoteInput);
        // On blur, do a full (visible) reconcile so the drawer shows the current gift lines.
        this._onNoteBlur = () => this.handleNoteBlur();
        this.noteTextarea.addEventListener('blur', this._onNoteBlur);
      }

      // Keep the ribbon quantity matched to product quantity whenever the cart changes
      // (e.g. the customer changes a product's quantity elsewhere in the drawer).
      this._onCartChange = () => this.matchRibbonQuantity();
      document.addEventListener('cart:change', this._onCartChange);
      document.addEventListener('cart:refresh', this._onCartChange);

      // On the cart page a product quantity change reloads the page, so correct the ribbon
      // quantity on load. The drawer does the same when it was rendered with only gift lines
      // left in the cart. If nothing needs fixing, matchRibbonQuantity returns without a
      // re-render, so there is no loop.
      if (this.isCartPage || this.hasAttribute('orphan-gift-lines')) {
        this.matchRibbonQuantity();
      }

      // A note typed just before tapping Checkout may not be in the cart yet (the sync is
      // debounced), so hold the checkout until it is.
      this.form = this.closest('form');
      if (this.form) {
        this._onFormSubmit = (event) => this.handleFormSubmit(event);
        this.form.addEventListener('submit', this._onFormSubmit);
      }
    }

    disconnectedCallback() {
      clearTimeout(this._noteDebounce);
      if (this.ribbonCheckbox && this._onRibbonChange) {
        this.ribbonCheckbox.removeEventListener('change', this._onRibbonChange);
      }
      if (this.noteTextarea) {
        if (this._onNoteInput) this.noteTextarea.removeEventListener('input', this._onNoteInput);
        if (this._onNoteBlur) this.noteTextarea.removeEventListener('blur', this._onNoteBlur);
      }
      document.removeEventListener('cart:change', this._onCartChange);
      document.removeEventListener('cart:refresh', this._onCartChange);
      if (this.form && this._onFormSubmit) {
        this.form.removeEventListener('submit', this._onFormSubmit);
      }
    }

    // If a note change hasn't reached the cart yet, wait for it, then go to checkout. The drawer
    // may re-render while syncing (replacing this form), so navigate rather than resubmit.
    handleFormSubmit(event) {
      const submitter = event.submitter;
      if (!submitter || submitter.name !== 'checkout') return;
      if (!this._noteDirty && this._pendingSyncs === 0) return;

      event.preventDefault();
      clearTimeout(this._noteDebounce);
      this._checkingOut = true;
      this.startLoading();

      this.syncNote().finally(() => {
        window.location.href = `${this.root}checkout`;
      });
    }

    get root() {
      return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    }

    // Total quantity of the real products in the cart (everything except our gift lines).
    productQuantity(cart) {
      return cart.items.reduce((sum, item) => {
        const isGift = item.properties && item.properties[GIFT_OPTION_PROPERTY];
        return isGift ? sum : sum + item.quantity;
      }, 0);
    }

    giftLine(cart, optionKey) {
      return cart.items.find(
        (item) => item.properties && item.properties[GIFT_OPTION_PROPERTY] === optionKey
      );
    }

    async handleRibbonToggle() {
      if (!this.ribbonVariantId || this._busy) return;

      this._busy = true;
      this.ribbonCheckbox.disabled = true;
      this.startLoading();

      try {
        if (this.ribbonCheckbox.checked) {
          await this.setRibbon(true, { silent: false });
        } else {
          await this.setRibbon(false, { silent: false });
        }
      } catch (err) {
        console.error('[gift-options] ribbon toggle failed', err);
      } finally {
        if (this.ribbonCheckbox && this.ribbonCheckbox.isConnected) {
          this.ribbonCheckbox.disabled = false;
        }
        this._busy = false;
        this.endLoading();
      }
    }

    handleNoteInput() {
      this._noteDirty = true;
      clearTimeout(this._noteDebounce);
      this._noteDebounce = setTimeout(() => this.syncNote(), NOTE_DEBOUNCE_MS);
    }

    async handleNoteBlur() {
      // Run any pending note sync right away. Because the field is no longer focused,
      // syncNote will do a full (visible) refresh itself, reconciling the drawer.
      clearTimeout(this._noteDebounce);
      await this.syncNote();
    }

    // Syncs run one after another, so a checkout waiting on syncNote() also waits for any
    // sync already in flight (e.g. the one started when the note field lost focus).
    syncNote() {
      this._pendingSyncs++;
      const run = this._syncChain.then(() => this.syncNoteNow());
      this._syncChain = run.catch(() => {}).finally(() => {
        this._pendingSyncs--;
      });
      return this._syncChain;
    }

    // Adds/removes the note line to match whether the note has text, saves the note text,
    // and auto-checks the ribbon when a note is added. Runs "silent" (no drawer re-render)
    // while the textarea is focused, so the cursor is never disturbed mid-typing.
    async syncNoteNow() {
      if (!this.noteVariantId || this._busy) return;

      this._noteDirty = false;
      const noteValue = this.noteTextarea.value;
      const hasContent = noteValue.trim().length > 0;
      const focused = document.activeElement === this.noteTextarea;

      const cart = await this.fetchCart();
      if (!cart) return;

      const noteLine = this.giftLine(cart, NOTE_KEY);
      const noteTextSaved = (cart.note || '') === noteValue;
      const wantRibbon = hasContent; // typing a note implies wanting the ribbon
      const ribbonLine = this.giftLine(cart, RIBBON_KEY);

      const needAddNote = hasContent && !noteLine;
      const needRemoveNote = !hasContent && noteLine;
      const needRibbon = wantRibbon && !ribbonLine;

      if (!needAddNote && !needRemoveNote && !needRibbon && noteTextSaved) {
        // Nothing to change now. If an earlier silent change (a gift line added/removed
        // while the customer was typing) still needs to be shown, do one visible refresh.
        // Otherwise do nothing — this avoids a needless reload/re-render when the field is
        // simply focused and blurred with no change.
        if (!focused && this._pendingReconcile) {
          this._pendingReconcile = false;
          await this.refreshCart({ silent: false });
        }
        return;
      }

      this._busy = true;
      this.startLoading();
      try {
        // While the field has focus nothing is re-rendered, so no sections are requested.
        // Otherwise only the final mutation carries them - earlier ones would render HTML
        // that the next call immediately makes stale.
        const render = !focused;
        let result = null;


        const addItems = [];
        if (needAddNote) {
          addItems.push({ id: this.noteVariantId, quantity: 1, properties: { [GIFT_OPTION_PROPERTY]: NOTE_KEY } });
        }
        if (needRibbon) {
          const qty = this.productQuantity(cart) || 1;
          addItems.push({ id: this.ribbonVariantId, quantity: qty, properties: { [GIFT_OPTION_PROPERTY]: RIBBON_KEY } });
          if (this.ribbonCheckbox) this.ribbonCheckbox.checked = true;
        }
        if (!noteTextSaved) {
          const isFinal = addItems.length === 0 && !needRemoveNote;
          result = await this.saveNoteText(noteValue, render && isFinal);
        }
        // hasContent gates these, so an add and a removal can never both be pending.
        if (addItems.length) {
          result = await this.addItems(addItems, render);
        }
        if (needRemoveNote) {
          result = await this.removeGiftLines([NOTE_KEY], cart, render);
        }

        // A silent change (made while still typing) must be reconciled visibly later, on blur.
        if (focused) {
          this._pendingReconcile = true;
        } else {
          this._pendingReconcile = false;
        }
        await this.refreshCart({ silent: focused }, result);
      } catch (err) {
        console.error('[gift-options] note sync failed', err);
      } finally {
        this._busy = false;
        this.endLoading();
      }
    }

    // Adds or removes the ribbon line, sized to the current product quantity.
    async setRibbon(wanted, options) {
      const cart = await this.fetchCart();
      if (!cart) return;

      const ribbonLine = this.giftLine(cart, RIBBON_KEY);
      const render = !(options && options.silent);

      if (wanted && !ribbonLine) {
        const qty = this.productQuantity(cart) || 1;
        const result = await this.addItems(
          [{ id: this.ribbonVariantId, quantity: qty, properties: { [GIFT_OPTION_PROPERTY]: RIBBON_KEY } }],
          render
        );
        await this.refreshCart(options, result);
      } else if (!wanted && ribbonLine) {
        // Hand over the cart we already hold - re-reading it here cost a round trip on
        // every un-tick.
        const result = await this.removeGiftLines([RIBBON_KEY], cart, render);
        await this.refreshCart(options, result);
      }
    }

    // Keeps the ribbon line quantity equal to the product quantity, and removes the ribbon and note
    // lines (and the note text) once no real products are left. Called on cart changes.
    // Never re-renders while the note is focused, and never runs re-entrantly.
    async matchRibbonQuantity() {
      if (this._busy) return;

      const cart = await this.fetchCart();
      if (!cart) return;

      const ribbonLine = this.giftLine(cart, RIBBON_KEY);
      const noteLine = this.giftLine(cart, NOTE_KEY);
      const target = this.productQuantity(cart);

      if (target === 0) {
        if (!ribbonLine && !noteLine) return;

        // No real products left: the gift lines have no remove control, so drop them here.
        this._busy = true;
        let removal = null;
        try {
          if (cart.note) {
            await this.saveNoteText('', false);
          }
          removal = await this.removeGiftLines([RIBBON_KEY, NOTE_KEY], cart, true);
        } finally {
          this._busy = false;
        }
        await this.refreshCart(undefined, removal);
        return;
      }

      if (!ribbonLine) return; // ribbon not active, nothing to match
      if (ribbonLine.quantity === target) return;

      this._busy = true;
      let result = null;
      try {
        result = await this.cartMutate('cart/change.js', { id: ribbonLine.key, quantity: target }, true);
      } finally {
        this._busy = false;
      }
      // Re-render so the corrected ribbon quantity shows. On the drawer this re-triggers
      // matchRibbonQuantity, but it finds the quantity already correct and returns early
      // without dispatching again, so there is no loop. On the cart page refreshCart reloads.
      await this.refreshCart(undefined, result);
    }

    startLoading() {
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
    }

    endLoading() {
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }

    async fetchCart() {
      try {
        const response = await fetch(`${this.root}cart.js`);
        if (!response.ok) return null;
        return await response.json();
      } catch (err) {
        console.error('[gift-options] failed to read cart', err);
        return null;
      }
    }

    // Ask the page which sections want re-rendering. This is the theme's own convention -
    // see its cart-quantity and add-to-cart code - and it lets the fresh drawer HTML ride back
    // on the mutation response instead of costing a second request to ?section_id=.
    bundledSections() {
      const sections = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } })
      );
      return sections.join(',');
    }

    // One cart mutation. Only the final call of a sequence should pass render:true - asking
    // earlier ones to render just produces HTML the next call immediately invalidates.
    async cartMutate(endpoint, body, render) {
      const payload = render ? Object.assign({}, body, { sections: this.bundledSections() }) : body;

      try {
        const response = await fetch(`${this.root}${endpoint}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(payload),
        });
        const json = await response.json().catch(() => ({}));

        if (!response.ok) {
          document.dispatchEvent(
            new CustomEvent('cart:error', { bubbles: true, detail: { message: json.description } })
          );
          return null;
        }
        return json;
      } catch (err) {
        console.error('[gift-options] ' + endpoint + ' failed', err);
        return null;
      }
    }

    async saveNoteText(note, render) {
      return this.cartMutate('cart/update.js', { note }, render);
    }

    async addItems(items, render) {
      return this.cartMutate('cart/add.js', { items }, render);
    }

    // Callers pass the cart they already hold; re-reading it here cost a round trip on every
    // removal. Only fall back to fetching when a caller genuinely has nothing.
    async removeGiftLines(optionKeys, cart, render) {
      const source = cart || (await this.fetchCart());
      if (!source) return null;

      const linesToRemove = source.items.filter(
        (item) => item.properties && optionKeys.includes(item.properties[GIFT_OPTION_PROPERTY])
      );

      // Remove each tagged line by its unique key, one at a time, so shifting positions
      // never cause us to target the wrong line. Only the last one renders.
      let result = null;
      for (let i = 0; i < linesToRemove.length; i++) {
        const isLast = i === linesToRemove.length - 1;
        result = await this.cartMutate(
          'cart/change.js',
          { id: linesToRemove[i].key, quantity: 0 },
          render && isLast
        );
      }
      return result;
    }

    get isCartPage() {
      return (
        window.themeVariables &&
        window.themeVariables.settings &&
        window.themeVariables.settings.pageType === 'cart'
      );
    }

    // options.silent: skip re-rendering the drawer body (used while the note field is
    // focused so typing is never interrupted). The gift line is still in the cart; the
    // drawer reconciles on blur via handleNoteBlur, or on the next cart change.
    // Hand the new cart to the theme. theme.js caches whatever is published here and reads
    // item_count off it, so a cart/add.js response - which carries only the items just added -
    // has to be topped up with a real cart first. Without sections there is nothing for the
    // drawer to swap in, so fall back to letting it fetch its own HTML.
    async publish(result) {
      if (!result || !result.sections || Object.keys(result.sections).length === 0) {
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        return;
      }

      let cart = result;
      if (!('item_count' in cart)) {
        const fresh = await this.fetchCart();
        if (!fresh) {
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          return;
        }
        fresh.sections = result.sections;
        cart = fresh;
      }

      // baseEvent is deliberately not "variant:add": the drawer force-opens itself on that
      // value, which would be wrong for a checkbox ticked inside the drawer.
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'line-item:change', cart },
        })
      );
    }

    async refreshCart(options, result) {
      const silent = options && options.silent;
      if (silent || this._checkingOut) return; // checkout navigates away; don't reload over it

      // The cart PAGE table is server-rendered and doesn't self-update from events, so the
      // theme reloads it after cart changes. Mirror that here (but never while the customer
      // is mid-note; that path passes silent:true and returns above).
      if (this.isCartPage) {
        window.location.reload();
        return;
      }

      if (result) {
        await this.publish(result);
        return;
      }

      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    }
  }

  window.customElements.define('gift-options', GiftOptions);
})();
